import React, { Suspense, lazy, useMemo } from 'react'
import { enableMapSet } from 'immer'
import Helmet from 'react-helmet'
import { Router } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'

import { AntdSpinDefaultIndicator } from 'src/antd/Spin/AntdDefaultIndicator'
import { GlobalStyles as StorybookStyles } from 'src/storybook/styles'
import { AppFont } from 'src/styles/AppFont'
import { ThemeVars } from 'src/styles/ThemeVars'
import { Toaster } from 'src/ui/toast/Toaster'

import AntdI18n from './AntdI18n'
import DayjsI18n from './DayjsI18n'
import { resolveEnvFavicon } from './envFavicon'
import { GlobalStyles, GlobalStylesAntFillViewList } from './global.styles'
import { createConfig } from './route-config'
import MainRouter from './router'
// FE-PERF-2 FE2.2: the app-shell chunk gate (login/main chunk load,
// right after the auth gate) was the second bare spinner of every
// cold boot; it now speaks the same page-frame skeleton as the auth
// gate and every route chunk.
import { BirdsEatBug } from './setupBirdsEatBug'
import Transifex from './Transifex'
import { WsDisconnectNotification } from '../components/graphql/ws'
import RouteChunkSkeleton from '../components/main/RouteChunkSkeleton'
import history from '../history'
import { BookingFollowUpHost } from '../modules/skip-hire/booking-follow-up/BookingFollowUpHost'
import RouterFactory, {
  useRouting,
} from '../reducers/routing/child-router-factory'
import { useHistoryStoreInit } from '../reducers/routing/store'
import { AntdMessageWithContextInit } from '../utils/antdMessageContext'
import AsyncModalPortal from '../utils/asyncModalAction'

/**
 * See: Immer docs on why we need this.
 * Note: Don't remove this until/unless you are sure that we are not using JS Map in any immer produce function
 * through out our app. If you remove it, then it will break those actions
 */
enableMapSet()

// Code-split the staff feedback widget (react-linear-feedback) so it — and its
// transitive modern-screenshot dependency — never land in the pre-login entry
// chunk (see the manualChunks discipline in vite.config.mts). Combined with the
// opt-in check below, users who haven't enabled it never download the chunk.
const FeedbackGate = lazy(() =>
  import('react-linear-feedback/react').then((m) => ({
    default: m.FeedbackGate,
  }))
)

// Mirrors FeedbackGate's own default gate (urlParam "feedback",
// cookieName "wh_feedback"): mount when the URL carries ?feedback (any value —
// so ?feedback=0 still mounts and lets the widget turn itself off) or the
// enabled cookie is already set. Otherwise it stays entirely unloaded.
const isFeedbackEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  if (new URLSearchParams(window.location.search).has('feedback')) return true
  // Exact per-cookie match (mirrors the library's own gate) so an unrelated
  // cookie like "x_wh_feedback=1" can't false-positive.
  return document.cookie.split(';').some((c) => c.trim() === 'wh_feedback=1')
}

const favIcon = resolveEnvFavicon()

const App = () => {
  const { routingMap: map } = useRouting()

  BirdsEatBug.useInit()
  useHistoryStoreInit()

  // PRO-443: createConfig(map) rebuilds the full route array (with JSX) on
  // every root render. Unmemoised, any root reactive-var change re-created the
  // whole config across both nested RouterFactory layers, churning app-wide
  // re-renders. Memoise by `map` identity so it only rebuilds when routing
  // actually changes.
  const routerConfig = useMemo(() => createConfig(map), [map])

  return (
    <>
      <div className="wh-print-insert" />
      <div className="mainHolder">
        <Helmet>
          <title>WasteHero Platform</title>
          <meta name="description" content="WasteHero" />
          <link rel="icon" type="image/png" href={favIcon} sizes="48x48" />
        </Helmet>
        <ThemeVars />
        <StorybookStyles />
        <GlobalStyles />
        {/* Geist, behind the design toggle. Renders nothing at all in antd
            mode. It sits after <GlobalStyles /> for readability only — its
            `html body` selector outranks that file's `body` rule on
            specificity, not on order. See src/styles/AppFont.tsx. */}
        <AppFont />
        {/* The app's spinner installed as antd's app-wide default indicator,
            behind the same toggle. Renders nothing, and writes nothing at all
            in antd mode. It is what reaches the Spins antd renders INSIDE
            Table and List — the only loading states the `src/antd/Spin` seam
            cannot switch, and the ones that kept painting the four blue dots
            after everything else had moved. See
            src/antd/Spin/AntdDefaultIndicator.tsx. */}
        <AntdSpinDefaultIndicator />
        {/* The app-wide toast host, behind the same toggle. Renders nothing
            at all in antd mode — antd's own notice holder stays the only one
            on the page — and in shadcn mode lazily mounts Sonner, which is
            where every `message.*` / `notification.*` call is then served.
            The dispatch itself is in src/ui/toast/index.ts. */}
        <Toaster />
        <GlobalStylesAntFillViewList defaultYScroller />
        <DayjsI18n />
        <Transifex />
        <Router history={history}>
          <>
            <AntdI18n>
              <MainRouter>
                <AsyncModalPortal />
                {/* The skip-hire booking follow-up (PRODUCT-525) renders
                    here from plain data in its own store — never as an
                    element inside the async-modal store (PRODUCT-545). */}
                <BookingFollowUpHost />
                <AntdMessageWithContextInit />
                <Suspense fallback={<RouteChunkSkeleton />}>
                  <RouterFactory
                    config={routerConfig}
                    defaultRedirect="/app/dashboard"
                  />
                </Suspense>
              </MainRouter>
            </AntdI18n>
            <WsDisconnectNotification />
            <ToastContainer
              enableMultiContainer
              position={toast.POSITION.BOTTOM_RIGHT}
            />
          </>
        </Router>
      </div>
      {/*
        Staff feedback widget (react-linear-feedback). Hidden by default;
        a staff member enables it for themselves by visiting any page with
        ?feedback (a cookie remembers the choice for 90 days). Once on, they
        drag a box over the screen, write a note, and it files a Linear issue
        with an annotated screenshot via the /api/feedback endpoint. The
        server gate (cookieGate("wh_feedback")) is what stops a stray public
        request reaching Linear on our key. Lazy + opt-in-gated so the widget
        and its screenshot lib never load for users who haven't enabled it.
      */}
      {isFeedbackEnabled() && (
        <Suspense fallback={null}>
          {/*
            No `position` prop: react-linear-feedback dropped it in 0.6.0 and
            the right-edge tab this app deliberately chose is now the widget's
            only (and built-in) launcher. See the guard test beside this file.

            `showPriority` (0.7.0) adds an Urgent/High/Medium/Low row to the
            composer, mapped to Linear's own priority scale and applied to the
            created issue by the handler. Nothing is preselected on purpose:
            submitting without a choice leaves the issue at "No priority".
          */}
          <FeedbackGate
            brandColor="#1890FF"
            screenshotMode="native"
            showPriority
          />
        </Suspense>
      )}
    </>
  )
}

export default App
